import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Container, 
  Typography,
  Box, 
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  IconButton,
  Dialog,
  DialogContent,
  InputAdornment,
  Skeleton,
  Avatar,
  Tooltip,
  useMediaQuery,
  useTheme,
  Grid,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Button,
  CircularProgress
} from '@mui/material';
import {
  Search as SearchIcon,
  PlayArrow as PlayArrowIcon,
  Pause as PauseIcon,
  OpenInNew as OpenInNewIcon,
  Close as CloseIcon,
  AccessTime as AccessTimeIcon,
  Album as AlbumIcon,
  MusicNote as MusicNoteIcon
} from '@mui/icons-material';

const TRACKS_PER_PAGE = 20; // Número de canciones a cargar por página

const MusicPage = ({ playlistId, title }) => {
  const { t } = useTranslation();
  // Estados para la playlist y las canciones
  const [tracks, setTracks] = useState([]);
  const [filteredTracks, setFilteredTracks] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [playlistDetails, setPlaylistDetails] = useState(null);
  
  // Estados para paginación
  const [offset, setOffset] = useState(0);
  const [hasMoreTracks, setHasMoreTracks] = useState(true);
  
  // Estados para la reproducción y el modal
  const [selectedTrack, setSelectedTrack] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [playingTrackId, setPlayingTrackId] = useState(null);
  
  // Referencias
  const audioRef = useRef(null);
  const observer = useRef();
  const lastTrackElementRef = useCallback(node => {
    if (loading || loadingMore) return;
    if (observer.current) observer.current.disconnect();
    
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMoreTracks && !searchTerm) {
        fetchMoreTracks();
      }
    }, { threshold: 0.5 });
    
    if (node) observer.current.observe(node);
  }, [loading, loadingMore, hasMoreTracks, searchTerm]);
  
  // Estilos responsivos
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  // Obtener datos iniciales de la playlist
  useEffect(() => {
    const fetchPlaylist = async () => {
      setLoading(true);
      setError(null);
      setTracks([]);
      setFilteredTracks([]);
      setOffset(0);
      setHasMoreTracks(true);
      
      try {
        // Endpoint con tu API Gateway y parámetros de paginación
        const endpoint = `https://api.labs.jeshua.ong/spotify/${playlistId}?offset=0&limit=${TRACKS_PER_PAGE}`;
        const response = await fetch(endpoint);
        
        if (!response.ok) {
          throw new Error(`Error ${response.status}: ${response.statusText}`);
        }
        
        const data = await response.json();
        
        // Guardar datos de la playlist y canciones
        if (data) {
          setTracks(data.tracks || []);
          setFilteredTracks(data.tracks || []);
          setHasMoreTracks(data.has_more || false);
          setOffset(data.next_offset || 0);
          
          // Guardar detalles de la playlist
          if (data.name) {
            setPlaylistDetails({
              name: data.name,
              description: data.description,
              image: data.image,
              followers: data.followers,
              totalTracks: data.total_tracks,
              externalUrl: data.external_url
            });
          }
        }
      } catch (err) {
        console.error('Error al cargar la playlist:', err);
        setError(t('music.error') + ': ' + err.message);
      } finally {
        setLoading(false);
      }
    };
    
    if (playlistId) {
      fetchPlaylist();
    }
    
    // Cleanup
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      setPlayingTrackId(null);
      if (observer.current) {
        observer.current.disconnect();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playlistId]);

  // Cargar más canciones cuando se desplaza hacia abajo
  const fetchMoreTracks = async () => {
    if (loadingMore || !hasMoreTracks || searchTerm) return;
    
    setLoadingMore(true);
    
    try {
      const endpoint = `https://api.labs.jeshua.ong/spotify/${playlistId}?offset=${offset}&limit=${TRACKS_PER_PAGE}`;
      const response = await fetch(endpoint);
      
      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      
      if (data && data.tracks) {
        setTracks(prevTracks => [...prevTracks, ...data.tracks]);
        
        if (!searchTerm) {
          setFilteredTracks(prevFiltered => [...prevFiltered, ...data.tracks]);
        }
        
        setHasMoreTracks(data.has_more || false);
        setOffset(data.next_offset || offset);
      }
    } catch (err) {
      console.error('Error al cargar más canciones:', err);
      // No cambiamos el estado de error principal para no interrumpir la experiencia
    } finally {
      setLoadingMore(false);
    }
  };

  // Filtrar canciones al escribir en el buscador
  useEffect(() => {
    if (tracks.length > 0) {
      if (searchTerm) {
        const lowercaseSearch = searchTerm.toLowerCase();
        const filtered = tracks.filter(track => 
          track.name.toLowerCase().includes(lowercaseSearch) ||
          track.artists?.some(artist => artist.name.toLowerCase().includes(lowercaseSearch)) ||
          (track.album && track.album.name.toLowerCase().includes(lowercaseSearch))
        );
        setFilteredTracks(filtered);
      } else {
        setFilteredTracks(tracks);
      }
    }
  }, [searchTerm, tracks]);

  // Formatear duración de milisegundos a mm:ss
  const formatDuration = (ms) => {
    if (!ms) return '--:--';
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // Obtener el nombre de los artistas concatenado
  const getArtistsNames = (artists) => {
    if (!artists || artists.length === 0) return '';
    return artists.map(artist => artist.name).join(', ');
  };

  // Handlers
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleTrackClick = (track) => {
    setSelectedTrack(track);
    setDialogOpen(true);

    setTimeout(() => {
      const playButton = document.querySelector('.dialog-content .play-button-indicator');
      if (playButton) {
        playButton.classList.add('animate-attention');
      }
    }, 500);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };
  
  const handlePlayPause = (track, event) => {
    event.stopPropagation();
    
    // Si hay un track reproduciéndose, pausarlo
    if (playingTrackId === track.id) {
      setPlayingTrackId(null);
      if (audioRef.current) {
        audioRef.current.pause();
      }
    } else {
      // Si hay otro track reproduciéndose, pausarlo primero
      if (audioRef.current) {
        audioRef.current.pause();
      }
      
      // Reproducir el nuevo track
      if (track.preview_url) {
        audioRef.current = new Audio(track.preview_url);
        audioRef.current.play()
          .then(() => {
            setPlayingTrackId(track.id);
            
            // Cuando termine la reproducción
            audioRef.current.onended = () => {
              setPlayingTrackId(null);
            };
          })
          .catch(error => {
            console.error("Error al reproducir:", error);
          });
      }
    }
  };
  
  // Componente SkeletonRow para loading
  const SkeletonRow = () => (
    <TableRow>
      <TableCell>
        <Skeleton variant="text" sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)' }} />
      </TableCell>
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Skeleton variant="circular" width={40} height={40} sx={{ mr: 2, bgcolor: 'rgba(255, 255, 255, 0.1)' }} />
          <Box>
            <Skeleton variant="text" width={150} sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)' }} />
            <Skeleton variant="text" width={100} sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)' }} />
          </Box>
        </Box>
      </TableCell>
      {!isMobile && (
        <TableCell>
          <Skeleton variant="text" width={120} sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)' }} />
        </TableCell>
      )}
      <TableCell align="right">
        <Skeleton variant="text" width={40} sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)' }} />
      </TableCell>
      <TableCell align="right">
        <Skeleton variant="circular" width={30} height={30} sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)' }} />
      </TableCell>
    </TableRow>
  );

  // Vista móvil - Lista en lugar de tabla
  const MobileTracksList = () => (
    <List sx={{ width: '100%', bgcolor: 'transparent', p: 0 }}>
      {filteredTracks.map((track, index) => {
        const isLastElement = index === filteredTracks.length - 1;
        
        return (
          <ListItem 
            key={track.id || index}
            alignItems="flex-start"
            ref={isLastElement && !searchTerm ? lastTrackElementRef : null}
            sx={{ 
              borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
              cursor: 'pointer',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.05)'
              },
              pr: 10,
              pl: 0
            }}
            onClick={() => handleTrackClick(track)}
            secondaryAction={
              <Box sx={{ display: 'flex', alignItems: 'center', position: 'absolute', right: 16, top: 'calc(50% - 12px)' }}>
                <Typography variant="body2" color="text.secondary" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  {formatDuration(track.duration_ms)}
                </Typography>
              </Box>
            }
          >
            <ListItemAvatar sx={{ m: 0 }}>
              <Avatar 
                src={track.album?.image || ''}
                variant="square"
                sx={{ width: 50, height: 50 }}
              >
                <MusicNoteIcon />
              </Avatar>
            </ListItemAvatar>
            <ListItemText
              primary={
                <Typography 
                  component="span" 
                  variant="body1" 
                  sx={{ 
                    color: 'white',
                    fontWeight: track.id === playingTrackId ? 'bold' : 'medium',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    display: 'block',
                    width: '100%'
                  }}
                >
                  {track.name}
                </Typography>
              }
              secondary={
                <Typography
                  component="span"
                  variant="body2"
                  sx={{ 
                    color: 'rgba(255, 255, 255, 0.7)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    display: 'block',
                    width: '100%'
                  }}
                >
                  {getArtistsNames(track.artists)}
                </Typography>
              }
              sx={{
                margin: 0,
                '.MuiListItemText-primary': {
                  marginBottom: '2px'
                },
                width: 'calc(100% - 16px)' // Asegurar que el texto no se superponga con la duración
              }}
            />
          </ListItem>
        );
      })}
    </List>
  );

  return (
    <Container component="main" sx={{ mt: 4, mb: 4, flexGrow: 1 }}>
      {/* Título de la playlist */}
      <Typography 
        variant="h4" 
        component="h1" 
        gutterBottom 
        align="center" 
        sx={{ 
          color: 'white',
          mb: 4,
          fontSize: { xs: '1.5rem', md: '2rem' }
        }}
      >
        {title || (playlistDetails?.name || 'Playlist')}
      </Typography>
      
      {/* Cabecera de la playlist con imagen y detalles */}
      {!loading && playlistDetails && (
        <Box sx={{ mb: 4 }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} sm={4} md={3}>
              <Box 
                component="img"
                src={playlistDetails.image || '/placeholder-album.jpg'}
                alt={playlistDetails.name}
                sx={{ 
                  width: '100%',
                  maxWidth: 250,
                  height: 'auto',
                  borderRadius: 1,
                  boxShadow: 3,
                  mx: 'auto',
                  display: 'block'
                }}
              />
            </Grid>
            <Grid item xs={12} sm={8} md={9}>
              {playlistDetails.description && (
                <Typography variant="body2" color="rgba(255,255,255,0.7)" gutterBottom>
                  {playlistDetails.description}
                </Typography>
              )}
              <Typography variant="body2" color="rgba(255,255,255,0.7)" sx={{ mt: 1 }}>
                {playlistDetails.totalTracks} canciones
                {playlistDetails.followers > 0 && ` • ${playlistDetails.followers.toLocaleString()} seguidores`}
              </Typography>
              {playlistDetails.externalUrl && (
                <Box sx={{ mt: 2 }}>
                  <IconButton 
                    href={playlistDetails.externalUrl} 
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ 
                      color: '#ff5722',
                      '&:hover': { bgcolor: 'rgba(255, 87, 34, 0.1)' }
                    }}
                  >
                    <OpenInNewIcon /> 
                    <Typography variant="body2" sx={{ ml: 1, color: 'white' }}>
                      Abrir en Spotify
                    </Typography>
                  </IconButton>
                </Box>
              )}
            </Grid>
          </Grid>
        </Box>
      )}
      
      {/* Mostrar error si ocurrió alguno */}
      {error && (
        <Box 
          sx={{ 
            backgroundColor: 'rgba(255, 87, 34, 0.1)', 
            color: 'white',
            p: 2,
            borderRadius: 1,
            mb: 4
          }}
        >
          <Typography>
            {error}
          </Typography>
        </Box>
      )}
      
      {/* Buscador de canciones */}
      {!loading && !error && tracks.length > 0 && (
        <TextField
          fullWidth
          variant="outlined"
          placeholder={t('music.search')}
          value={searchTerm}
          onChange={handleSearchChange}
          sx={{
            mb: 4,
            "& .MuiInputBase-input": {
              color: "#fff"
            },
            "& .MuiOutlinedInput-root": {
              "& fieldset": {
                borderColor: "#fff"
              },
              "&:hover fieldset": {
                borderColor: "#fff"
              },
              "&.Mui-focused fieldset": {
                borderColor: "#fff"
              }
            },
            "& .MuiInputBase-input::placeholder": {
              color: "#fff",
              opacity: 0.7
            }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: '#fff' }} />
              </InputAdornment>
            ),
          }}
        />
      )}
      
      {/* Título de la sección de canciones */}
      {!loading && !error && (
        <Typography 
          variant="h6" 
          component="h2" 
          gutterBottom 
          color="white" 
          sx={{ 
            mb: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}
        >
          <MusicNoteIcon sx={{ color: '#ff5722' }} />
          {searchTerm 
            ? `Resultados de búsqueda (${filteredTracks.length})` 
            : `Canciones (${filteredTracks.length} de ${playlistDetails?.totalTracks || '?'})`
          }
        </Typography>
      )}
      
      {/* Visualización móvil */}
      {isMobile && !loading && !error && filteredTracks.length > 0 && (
        <MobileTracksList />
      )}
      
      {/* Visualización desktop - Tabla de canciones */}
      {!isMobile && (
        <TableContainer 
          component={Paper} 
          sx={{ 
            backgroundColor: 'transparent',
            boxShadow: 'none'
          }}
        >
          <Table sx={{ minWidth: 650 }} aria-label="playlist tracks">
            <TableHead>
              <TableRow>
                <TableCell sx={{ color: 'rgba(255, 255, 255, 0.7)', width: '40px', borderBottom: '1px solid rgba(255, 255, 255, 0.2)' }}>#</TableCell>
                <TableCell sx={{ color: 'rgba(255, 255, 255, 0.7)', borderBottom: '1px solid rgba(255, 255, 255, 0.2)' }}>{t('music.title_col')}</TableCell>
                {!isTablet && (
                  <TableCell sx={{ color: 'rgba(255, 255, 255, 0.7)', borderBottom: '1px solid rgba(255, 255, 255, 0.2)' }}>
                    <AlbumIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 1 }} />
                    {t('music.album_col')}
                  </TableCell>
                )}
                <TableCell align="right" sx={{ color: 'rgba(255, 255, 255, 0.7)', borderBottom: '1px solid rgba(255, 255, 255, 0.2)' }}>
                  <AccessTimeIcon fontSize="small" />
                </TableCell>
                <TableCell align="right" sx={{ width: '50px', borderBottom: '1px solid rgba(255, 255, 255, 0.2)' }}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                // Skeletons durante la carga inicial
                Array(8).fill().map((_, index) => (
                  <SkeletonRow key={`skeleton-${index}`} />
                ))
              ) : filteredTracks.length > 0 ? (
                // Filas de canciones
                filteredTracks.map((track, index) => {
                  const isLastElement = index === filteredTracks.length - 1;
                  
                  return (
                    <TableRow
                      key={track.id || index}
                      hover
                      ref={isLastElement && !searchTerm ? lastTrackElementRef : null}
                      onClick={() => handleTrackClick(track)}
                      sx={{ 
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.05)'
                        },
                        '& .MuiTableCell-root': {
                          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                          color: 'white'
                        }
                      }}
                    >
                      <TableCell component="th" scope="row">
                        {index + 1}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Avatar
                            variant="square"
                            src={track.album?.image || ''}
                            alt={track.name}
                            sx={{ width: 40, height: 40, mr: 2 }}
                          >
                            <MusicNoteIcon />
                          </Avatar>
                          <Box>
                            <Typography 
                              variant="body1" 
                              sx={{ 
                                color: 'white', 
                                fontWeight: track.id === playingTrackId ? 'bold' : 'normal' 
                              }}
                            >
                              {track.name}
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                              {getArtistsNames(track.artists)}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      {!isTablet && (
                        <TableCell>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: 'rgba(255, 255, 255, 0.7)',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              maxWidth: 200
                            }}
                          >
                            {track.album?.name || t('music.unknown')}
                          </Typography>
                        </TableCell>
                      )}
                      <TableCell align="right">
                        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                          {formatDuration(track.duration_ms)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title={playingTrackId === track.id ? t('music.pause') : t('music.play')}>
                          <span>
                            <IconButton
                              size="small"
                              onClick={(e) => handlePlayPause(track, e)}
                              disabled={!track.preview_url}
                              sx={{ 
                                color: '#ff5722',
                                '&:hover': {
                                  backgroundColor: 'rgba(255, 87, 34, 0.1)',
                                },
                                '&.Mui-disabled': {
                                  color: 'rgba(255, 255, 255, 0.3)',
                                }
                              }}
                            >
                              {playingTrackId === track.id ? <PauseIcon /> : <PlayArrowIcon />}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ color: 'white' }}>
                    <Typography variant="body1">
                      No se encontraron canciones que coincidan con tu búsqueda.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      
      {/* Sin resultados en móvil */}
      {isMobile && !loading && !error && filteredTracks.length === 0 && (
        <Box width="100%" textAlign="center" my={4}>
          <Typography variant="body1" color="white">
            No se encontraron canciones que coincidan con tu búsqueda.
          </Typography>
        </Box>
      )}
      
      {/* Indicador de carga para "cargar más" */}
      {loadingMore && (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress color="primary" size={30} />
        </Box>
      )}
      
      {/* Mensaje cuando se han cargado todas las canciones */}
      {!loading && !loadingMore && !hasMoreTracks && tracks.length > 0 && !searchTerm && (
        <Box width="100%" textAlign="center" my={4}>
          <Typography variant="body2" color="rgba(255, 255, 255, 0.7)">
            Has llegado al final de la lista de canciones.
          </Typography>
        </Box>
      )}
      
      {/* Botón de cargar más (alternativa al scroll infinito) */}
      {!loading && !loadingMore && hasMoreTracks && tracks.length > 0 && !searchTerm && (
        <Box width="100%" textAlign="center" my={4}>
          <Button
            variant="outlined"
            color="primary"
            onClick={fetchMoreTracks}
            startIcon={loadingMore ? <CircularProgress size={20} color="inherit" /> : null}
            sx={{
              borderColor: '#ff5722',
              color: '#ff5722',
              '&:hover': {
                borderColor: '#e64a19',
                backgroundColor: 'rgba(255, 87, 34, 0.1)',
              }
            }}
          >
            Cargar más canciones
          </Button>
        </Box>
      )}
      
      {/* Diálogo para reproducir la canción */}
      <Dialog
        fullWidth
        maxWidth="sm"
        open={dialogOpen}
        onClose={handleDialogClose}
        PaperProps={{
          sx: {
            backgroundColor: '#424a4a',
            color: 'white',
            m: { xs: 1, sm: 2 },
            width: '100%',
            height: 'auto',
            maxHeight: '400px', // Ajustado al no necesitar tanto espacio inferior
            '@media (max-width: 367px)': {
              maxHeight: '280px'
            }
          }
        }}
      >
        <DialogContent 
          sx={{ 
            p: 0, 
            position: 'relative',
            overflow: 'hidden',
            pb: 4,
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          {selectedTrack && (
            <Box sx={{ textAlign: 'center', width: '100%' }}>
              <Typography variant="body1" sx={{ 
                color: '#ff5722', 
                fontWeight: 'bold',
                animation: 'pulse 1.5s infinite',
                '@keyframes pulse': {
                  '0%': { opacity: 0.7 },
                  '50%': { opacity: 1 },
                  '100%': { opacity: 0.7 },
                },
                pt: 2
              }}>
                Haz clic en Play para escuchar
              </Typography>
              <Box 
                sx={{ 
                  width: '100%',
                  pt: '100%',
                  position: 'relative'
                }}
              >
                <iframe
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    borderRadius: '12px',
                    width: '100%',
                    height: '100%',
                    border: 0
                  }}
                  src={`https://open.spotify.com/embed/track/${selectedTrack.id}?utm_source=generator&theme=0&autoplay=1`}
                  allowFullScreen
                  allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                  loading="lazy"
                  title={selectedTrack.name}
                ></iframe>
              </Box>
              
              {/* Botón de cerrar reposicionado justo debajo del iframe */}
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  display: 'flex',
                  justifyContent: 'center',
                  zIndex: 1
                }}
              >
                <IconButton
                  onClick={handleDialogClose}
                  aria-label={t('music.close')}
                  sx={{
                    backgroundColor: '#ff5722',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: '#e64a19',
                    },
                    transform: 'translateY(-50%)', // Mover hacia arriba para que coincida con el borde inferior del iframe
                    boxShadow: '0px 2px 4px rgba(0,0,0,0.3)'
                  }}
                  size="large"
                >
                  <CloseIcon />
                </IconButton>
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Container>
  );
};

export default MusicPage;